import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  RefreshCw, Plus, Trash2, Send, Link2, Pencil, X, FileText, DollarSign,
  Download, Mail, Truck, PackageCheck, CheckCircle2, ShoppingCart, MapPin, Copy,
} from 'lucide-react';

import { SearchPicker } from './SearchPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATALOG_GROUPS = { peptide: 'Peptides', shop: 'Accessories', wholesale: 'Wholesale Kits' };

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-amber-100 text-amber-700',
  reserved: 'bg-green-100 text-green-700',
  expired: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
};

const PAY_STATES = [{ v: 'paid', lbl: 'Paid' }, { v: 'deposit', lbl: 'Deposit' }, { v: 'reserved', lbl: 'Reserved (awaiting)' }];
const DELIVERY_METHODS = [{ v: 'shipping', lbl: 'Shipping' }, { v: 'local_delivery', lbl: 'Local delivery' }];
const STATUS_FILTERS = [{ v: 'all', lbl: 'All statuses' }, { v: 'draft', lbl: 'Draft' }, { v: 'sent', lbl: 'Sent' }, { v: 'viewed', lbl: 'Viewed' }, { v: 'reserved', lbl: 'Reserved' }, { v: 'expired', lbl: 'Expired' }];

const emptyItem = () => ({
  name: '', product_id: '', vial_size: '', unit_price: '', quantity: 1,
  discount_type: 'none', discount_value: '',
});

const emptyForm = () => ({
  recipient_name: '', recipient_email: '', recipient_phone: '',
  note: '', shipping_cost: '', expiry_days: 14, items: [emptyItem()],
});

const lineTotals = (it) => {
  const gross = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity, 10) || 0);
  let discount = 0;
  if (it.discount_type === 'percentage') discount = gross * Math.min(parseFloat(it.discount_value) || 0, 100) / 100;
  else if (it.discount_type === 'fixed') discount = Math.min(parseFloat(it.discount_value) || 0, gross);
  return { gross, discount, total: Math.max(0, gross - discount) };
};

const AdminCustomOrdersTab = ({ adminFetch }) => {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [catalog, setCatalog] = React.useState([]);
  const [customers, setCustomers] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [fulfillOpen, setFulfillOpen] = React.useState(null);
  const [tracking, setTracking] = React.useState({ tracking_number: '', carrier_code: 'usps' });
  const [busyId, setBusyId] = React.useState(null);
  const [convert, setConvert] = React.useState(null); // the order being converted
  const emptyConvert = (o) => ({
    payment_state: 'paid',
    delivery_method: 'shipping',
    payment_method: 'other',
    payment_method_label: '',
    deposit_amount: '',
    apply_credit: false,
    credit_amount: '',
    accrue_affiliate_points: false,
    phone: o?.recipient_phone || '',
    shipping_address: { name: o?.recipient_name || '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '' },
  });
  const [convertForm, setConvertForm] = React.useState(emptyConvert(null));
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [history, setHistory] = React.useState([]);
  const [confirmOpen, setConfirmOpen] = React.useState(null);
  const [confirmCredit, setConfirmCredit] = React.useState({ apply: false, amount: '', accrue: false });
  const [partialOpen, setPartialOpen] = React.useState(null);
  const [partialAmount, setPartialAmount] = React.useState('');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders`);
      setOrders(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAux = async () => {
    try {
      const [c, cu] = await Promise.all([
        adminFetch(`${API_URL}/api/admin/custom-orders/catalog`).then((r) => r.json()),
        adminFetch(`${API_URL}/api/admin/customers`).then((r) => r.json()),
      ]);
      setCatalog(Array.isArray(c) ? c : []);
      setCustomers(Array.isArray(cu) ? cu : []);
    } catch (e) { console.error(e); }
  };

  const fetchHistory = async (email, excludeId) => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !e.includes('@')) { setHistory([]); return; }
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/history?email=${encodeURIComponent(e)}`);
      const data = await res.json();
      setHistory((Array.isArray(data) ? data : []).filter((x) => x.custom_order_id !== excludeId));
    } catch (err) { setHistory([]); }
  };

  React.useEffect(() => { fetchOrders(); fetchAux(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(emptyForm()); setEditId(null); setHistory([]); setShowForm(true); };

  const openEdit = (o) => {
    setForm({
      recipient_name: o.recipient_name || '',
      recipient_email: o.recipient_email || '',
      recipient_phone: o.recipient_phone || '',
      note: o.note || '',
      shipping_cost: o.shipping_cost ? String(o.shipping_cost) : '',
      expiry_days: o.expiry_days || 14,
      items: (o.items || []).map((it) => ({
        name: it.name || '', product_id: it.product_id || '', vial_size: it.vial_size || '',
        unit_price: String(it.unit_price ?? ''), quantity: it.quantity || 1,
        discount_type: it.discount_type || 'none', discount_value: it.discount_value ? String(it.discount_value) : '',
      })),
    });
    setEditId(o.custom_order_id);
    fetchHistory(o.recipient_email, o.custom_order_id);
    setShowForm(true);
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  };

  const pickCatalog = (idx, value) => {
    if (!value) { updateItem(idx, { product_id: '' }); return; }
    const opt = catalog[parseInt(value, 10)];
    if (opt) updateItem(idx, { name: opt.name, product_id: opt.product_id, vial_size: opt.vial_size, unit_price: String(opt.price) });
  };

  const customerOptions = React.useMemo(() => customers.filter((c) => c.email).map((c) => ({
    key: c.email,
    label: c.name || c.email,
    sublabel: [c.email, c.phone, c.is_wholesale ? 'Wholesale' : ''].filter(Boolean).join(' · '),
    search: `${c.name || ''} ${c.email} ${c.phone || ''}`,
  })), [customers]);

  const catalogOptions = React.useMemo(() => catalog.map((o, i) => ({
    key: String(i),
    label: `${o.name}${o.vial_size ? ` (${o.vial_size})` : ''}`,
    sublabel: `$${Number(o.price).toFixed(2)}${o.code ? ` · ${o.code}` : ''}`,
    group: CATALOG_GROUPS[o.type] || 'Other',
    search: `${o.name} ${o.vial_size} ${o.code || ''} ${o.category || ''} ${CATALOG_GROUPS[o.type] || ''}`,
  })), [catalog]);

  const catalogKeyFor = (it) => {
    if (!it.product_id) return '';
    const i = catalog.findIndex((o) => o.product_id === it.product_id && (o.vial_size || '') === (it.vial_size || ''));
    return i >= 0 ? String(i) : '';
  };

  const pickCustomer = (email) => {
    const c = customers.find((x) => x.email === email);
    if (c) {
      setForm((f) => ({ ...f, recipient_email: c.email || '', recipient_name: c.name || f.recipient_name, recipient_phone: c.phone || f.recipient_phone }));
      fetchHistory(c.email, editId);
    }
  };

  const totals = React.useMemo(() => {
    let sub = 0; let disc = 0;
    form.items.forEach((it) => { const t = lineTotals(it); sub += t.gross; disc += t.discount; });
    const ship = parseFloat(form.shipping_cost) || 0;
    return { sub, disc, ship, total: Math.max(0, sub - disc + ship) };
  }, [form]);

  const save = async () => {
    if (!form.recipient_name.trim() || !form.recipient_email.trim()) { toast.error('Recipient name and email are required'); return; }
    const items = form.items.filter((it) => it.name.trim() && (parseFloat(it.unit_price) || 0) > 0);
    if (!items.length) { toast.error('Add at least one line item with a name and price'); return; }
    setSaving(true);
    try {
      const payload = {
        recipient_name: form.recipient_name.trim(),
        recipient_email: form.recipient_email.trim(),
        recipient_phone: form.recipient_phone.trim(),
        note: form.note.trim(),
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        expiry_days: parseInt(form.expiry_days, 10) || 14,
        items: items.map((it) => ({
          name: it.name.trim(), product_id: it.product_id || null, vial_size: it.vial_size,
          unit_price: parseFloat(it.unit_price) || 0, quantity: parseInt(it.quantity, 10) || 1,
          discount_type: it.discount_type, discount_value: parseFloat(it.discount_value) || 0,
        })),
      };
      const url = editId ? `${API_URL}/api/admin/custom-orders/${editId}` : `${API_URL}/api/admin/custom-orders`;
      const res = await adminFetch(url, { method: editId ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      toast.success(editId ? 'Custom order updated' : 'Custom order created');
      setShowForm(false);
      fetchOrders();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const sendEmail = async (o) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/send`, { method: 'POST' });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Send failed'); }
      toast.success(`Emailed to ${o.recipient_email}`);
      fetchOrders();
    } catch (e) { toast.error(e.message); }
  };

  const copyLink = (o) => {
    const link = `${window.location.origin}/custom-order/${o.token}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed'));
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete custom order for ${o.recipient_name}?`)) return;
    try {
      await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchOrders();
    } catch (e) { toast.error('Delete failed'); }
  };

  const duplicate = async (o) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/duplicate`, { method: 'POST' });
      if (!res.ok) throw new Error('Duplicate failed');
      toast.success(`Duplicated — new draft for ${o.recipient_name}`);
      fetchOrders();
    } catch (e) { toast.error(e.message); }
  };

  const renew = async (o) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/renew`, { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Renew failed');
      toast.success('Quote renewed');
      fetchOrders();
    } catch (e) { toast.error(e.message); }
  };

  const sendTest = async (o) => {
    const email = window.prompt('Send a TEST copy of this quote to which email?', o.recipient_email || '');
    if (!email) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/send-test`, {
        method: 'POST', body: JSON.stringify({ email }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Test send failed'); }
      toast.success(`Test quote sent to ${email}`);
    } catch (e) { toast.error(e.message); }
  };

  const downloadPdf = async (o) => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/pdf`);
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote_${o.custom_order_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download PDF'); }
  };

  // ---- Fulfillment on the linked reserved order ----
  const orderAction = async (o, label, fn) => {
    if (!o.reserved_order_id) return;
    setBusyId(o.custom_order_id);
    try {
      const res = await fn(o.reserved_order_id);
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `${label} failed`); }
      toast.success(label);
      fetchOrders();
    } catch (e) { toast.error(e.message); }
    setBusyId(null);
  };

  const confirmPayment = (o) => {
    const credit = Number(o.customer_store_credit || 0);
    const amt = confirmCredit.apply ? (confirmCredit.amount === '' ? credit : (parseFloat(confirmCredit.amount) || 0)) : 0;
    orderAction(o, 'Payment confirmed', () =>
      adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/confirm-payment`, {
        method: 'POST',
        body: JSON.stringify({ apply_credit: confirmCredit.apply, credit_amount: confirmCredit.apply && confirmCredit.amount !== '' ? amt : undefined, accrue_affiliate_points: confirmCredit.accrue }),
      })).then(() => { setConfirmOpen(null); setConfirmCredit({ apply: false, amount: '', accrue: false }); });
  };

  const markPreparing = (o) => orderAction(o, 'Marked as being prepared', (oid) =>
    adminFetch(`${API_URL}/api/admin/orders/${oid}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'confirmed', notify_customer: true }) }));

  const markShipped = (o) => {
    if (!tracking.tracking_number.trim()) { toast.error('Enter a tracking number'); return; }
    orderAction(o, 'Marked shipped & customer notified', (oid) =>
      adminFetch(`${API_URL}/api/admin/orders/${oid}/tracking`, {
        method: 'PATCH',
        body: JSON.stringify({ tracking_number: tracking.tracking_number.trim(), carrier_code: tracking.carrier_code, notify_customer: true }),
      })).then(() => { setFulfillOpen(null); setTracking({ tracking_number: '', carrier_code: 'usps' }); });
  };

  const markDelivered = (o) => orderAction(o, 'Marked delivered', () =>
    adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/mark-delivered`, { method: 'POST' }));

  const markBalancePaid = (o) => orderAction(o, 'Balance marked paid', () =>
    adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/mark-balance-paid`, { method: 'POST' }));

  const recordPartialPayment = (o) => {
    const total = Number((o.linked_order && o.linked_order.total) || o.total || 0);
    const amt = parseFloat(partialAmount);
    if (!amt || amt <= 0) { toast.error('Enter a payment amount greater than $0'); return; }
    if (amt >= total) { toast.error(`Amount must be less than the order total ($${total.toFixed(2)}). Use "Confirm Payment" for a full payment.`); return; }
    orderAction(o, 'Partial payment recorded', () =>
      adminFetch(`${API_URL}/api/admin/custom-orders/${o.custom_order_id}/record-partial-payment`, {
        method: 'POST',
        body: JSON.stringify({ amount: amt }),
      })).then(() => { setPartialOpen(null); setPartialAmount(''); });
  };

  const openConvert = (o) => { setConvertForm(emptyConvert(o)); setConvert(o); };

  const submitConvert = async () => {
    const a = convertForm.shipping_address;
    if (!a.address_line1.trim() || !a.city.trim() || !a.state.trim() || !a.zip_code.trim()) {
      toast.error('Please complete the address'); return;
    }
    const qTotal = Number(convert.total || 0);
    const credit = Number(convert.customer_store_credit || 0);
    const wantCredit = convertForm.apply_credit ? (convertForm.credit_amount === '' ? credit : (parseFloat(convertForm.credit_amount) || 0)) : 0;
    const applied = Math.max(0, Math.min(wantCredit, credit, qTotal));
    const dueAfterCredit = Math.max(0, qTotal - applied);
    const deposit = parseFloat(convertForm.deposit_amount) || 0;
    if (convertForm.payment_state === 'deposit' && (deposit <= 0 || deposit >= dueAfterCredit)) {
      toast.error(`Deposit must be greater than 0 and less than $${dueAfterCredit.toFixed(2)}`); return;
    }
    setBusyId(convert.custom_order_id);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/custom-orders/${convert.custom_order_id}/convert`, {
        method: 'POST',
        body: JSON.stringify({
          payment_state: convertForm.payment_state,
          delivery_method: convertForm.delivery_method,
          payment_method: convertForm.payment_method,
          payment_method_label: convertForm.payment_method_label,
          deposit_amount: convertForm.payment_state === 'deposit' ? deposit : undefined,
          apply_credit: convertForm.apply_credit,
          credit_amount: convertForm.apply_credit && convertForm.credit_amount !== '' ? (parseFloat(convertForm.credit_amount) || 0) : undefined,
          accrue_affiliate_points: convertForm.accrue_affiliate_points,
          phone: convertForm.phone,
          shipping_address: { ...a, name: a.name || convert.recipient_name },
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Convert failed'); }
      const d = await res.json();
      const stateLbl = { paid: 'Paid', deposit: 'Deposit', reserved: 'Reserved' }[convertForm.payment_state] || 'Reserved';
      toast.success(`Converted to order · ${stateLbl}${d.store_credit_used > 0 ? ` · $${d.store_credit_used} credit applied` : ''}`);
      setConvert(null);
      fetchOrders();
    } catch (e) { toast.error(e.message); }
    setBusyId(null);
  };

  const fulfillmentStage = (lo) => {
    if (!lo) return { label: 'Awaiting payment', cls: 'bg-amber-100 text-amber-700' };
    const st = lo.status || lo.order_status;
    if (st === 'delivered') return { label: 'Delivered', cls: 'bg-green-100 text-green-700' };
    if (st === 'shipped' || lo.tracking_number) return { label: 'Shipped', cls: 'bg-green-100 text-green-700' };
    const paid = lo.payment_status === 'paid' || lo.payment_status === 'confirmed' || lo.payment_status === 'partially_paid';
    if (paid) return { label: 'Being prepared', cls: 'bg-blue-100 text-blue-700' };
    return { label: 'Awaiting payment', cls: 'bg-amber-100 text-amber-700' };
  };

  const filteredOrders = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (q && !(`${o.recipient_name} ${o.recipient_email}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [orders, search, statusFilter]);

  return (
    <div className="space-y-6" data-testid="custom-orders-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-700" /> Custom Orders
          </h2>
          <p className="text-sm text-slate-500">Build bespoke bulk / single-vial quotes with custom pricing and email them to customers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="custom-orders-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={openNew} data-testid="new-custom-order-btn">
            <Plus className="h-4 w-4 mr-1" /> New Custom Order
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="border-2 border-teal-200" data-testid="custom-order-form">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{editId ? 'Edit Custom Order' : 'New Custom Order'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Pick existing customer</Label>
                <SearchPicker
                  options={customerOptions}
                  value={form.recipient_email}
                  onSelect={pickCustomer}
                  placeholder="Search customer…"
                  searchPlaceholder="Type name, email or phone…"
                  emptyText="No customer found"
                  testId="custom-order-customer-picker"
                />
              </div>
              <div>
                <Label className="text-xs">Recipient name *</Label>
                <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="custom-order-name" />
              </div>
              <div>
                <Label className="text-xs">Recipient email *</Label>
                <Input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} onBlur={(e) => fetchHistory(e.target.value, editId)} data-testid="custom-order-email" />
              </div>
            </div>

            {history.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="custom-order-history">
                <p className="text-xs font-semibold text-slate-600 mb-2">Past custom orders for this customer ({history.length})</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {history.map((h) => (
                    <div key={h.custom_order_id} className="flex items-center justify-between text-xs text-slate-600">
                      <span>{(h.created_at || '').slice(0, 10)} · {h.item_count} item(s) · <span className={`capitalize px-1.5 py-0.5 rounded ${STATUS_STYLES[h.status] || 'bg-slate-100'}`}>{h.status}</span></span>
                      <span className="font-medium text-slate-800">${Number(h.total).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} data-testid="custom-order-phone" />
              </div>
              <div>
                <Label className="text-xs">Shipping cost ($)</Label>
                <Input type="number" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} placeholder="0.00" data-testid="custom-order-shipping" />
              </div>
              <div>
                <Label className="text-xs">Valid for (days)</Label>
                <Input type="number" min="1" value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: e.target.value })} placeholder="14" data-testid="custom-order-expiry" />
              </div>
            </div>

            {/* Line items */}
            <div className="space-y-3">
              <Label className="text-xs font-semibold text-slate-700">Line items</Label>
              {form.items.map((it, idx) => {
                const t = lineTotals(it);
                return (
                  <div key={idx} className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`custom-order-item-${idx}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                      <div className="sm:col-span-3">
                        <Label className="text-[11px] text-slate-500">Pick product</Label>
                        <SearchPicker
                          options={catalogOptions}
                          value={catalogKeyFor(it)}
                          onSelect={(k) => pickCatalog(idx, k)}
                          placeholder="— Free form —"
                          searchPlaceholder="Search products & wholesale kits…"
                          emptyText="No product found"
                          clearLabel="— Free form (type your own) —"
                          testId={`custom-order-item-catalog-${idx}`}
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <Label className="text-[11px] text-slate-500">Item name *</Label>
                        <Input value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} data-testid={`custom-order-item-name-${idx}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] text-slate-500">Variant/size</Label>
                        <Input value={it.vial_size} onChange={(e) => updateItem(idx, { vial_size: e.target.value })} data-testid={`custom-order-item-variant-${idx}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] text-slate-500">Unit price ($) *</Label>
                        <Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`custom-order-item-price-${idx}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] text-slate-500">Qty</Label>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`custom-order-item-qty-${idx}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                      <div className="sm:col-span-3">
                        <Label className="text-[11px] text-slate-500">Discount</Label>
                        <select className="w-full border rounded-md h-9 px-2 text-sm" value={it.discount_type} onChange={(e) => updateItem(idx, { discount_type: e.target.value })} data-testid={`custom-order-item-disctype-${idx}`}>
                          <option value="none">No discount</option>
                          <option value="percentage">% off</option>
                          <option value="fixed">$ off</option>
                        </select>
                      </div>
                      {it.discount_type !== 'none' && (
                        <div className="sm:col-span-2">
                          <Label className="text-[11px] text-slate-500">{it.discount_type === 'percentage' ? '% value' : '$ value'}</Label>
                          <Input type="number" value={it.discount_value} onChange={(e) => updateItem(idx, { discount_value: e.target.value })} data-testid={`custom-order-item-discval-${idx}`} />
                        </div>
                      )}
                      <div className="sm:col-span-5 text-sm text-slate-600">
                        Line total: <span className="font-semibold text-slate-900">${t.total.toFixed(2)}</span>
                        {t.discount > 0 && <span className="text-teal-700 ml-2">(−${t.discount.toFixed(2)})</span>}
                      </div>
                      <div className="sm:col-span-2 flex justify-end">
                        {form.items.length > 1 && (
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} data-testid={`custom-order-item-remove-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))} data-testid="custom-order-add-item">
                <Plus className="h-4 w-4 mr-1" /> Add item
              </Button>
            </div>

            <div>
              <Label className="text-xs">Note to customer (optional)</Label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="custom-order-note" />
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div className="text-sm text-slate-600 space-y-0.5">
                <div>Subtotal: <span className="font-medium">${totals.sub.toFixed(2)}</span></div>
                {totals.disc > 0 && <div className="text-teal-700">Discount: −${totals.disc.toFixed(2)}</div>}
                {totals.ship > 0 && <div>Shipping: ${totals.ship.toFixed(2)}</div>}
                <div className="text-lg font-bold text-slate-900" data-testid="custom-order-form-total">Total: ${totals.total.toFixed(2)}</div>
              </div>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white" onClick={save} disabled={saving} data-testid="custom-order-save">
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Custom Order'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + status filter */}
      {orders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="h-9" data-testid="custom-order-search" />
          </div>
          <select className="border rounded-md h-9 px-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="custom-order-status-filter">
            {STATUS_FILTERS.map((s) => (<option key={s.v} value={s.v}>{s.lbl}</option>))}
          </select>
          <span className="text-xs text-slate-400" data-testid="custom-order-filter-count">{filteredOrders.length} of {orders.length}</span>
        </div>
      )}

      {/* Orders list */}
      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : orders.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500">
          <DollarSign className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          No custom orders yet. Click "New Custom Order" to build one.
        </CardContent></Card>
      ) : filteredOrders.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500 text-sm">No custom orders match your search / filter.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((o) => {
            const stage = o.status === 'reserved' ? fulfillmentStage(o.linked_order) : null;
            const lo = o.linked_order;
            const paid = lo && (lo.payment_status === 'paid' || lo.payment_status === 'confirmed');
            const delivered = lo && (lo.status === 'delivered' || lo.order_status === 'delivered');
            const isLocal = lo && lo.delivery_method === 'local_delivery';
            const shipped = lo && !delivered && (lo.status === 'shipped' || lo.order_status === 'shipped' || lo.tracking_number);
            const isExpired = o.status === 'expired';
            const canSend = o.status !== 'reserved' && !isExpired;
            const partial = lo && lo.payment_status === 'partially_paid';
            return (
            <Card key={o.custom_order_id} data-testid={`custom-order-row-${o.custom_order_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{o.recipient_name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[o.status] || 'bg-slate-100 text-slate-700'}`}>{o.status}</span>
                      {stage && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stage.cls}`} data-testid={`custom-order-stage-${o.custom_order_id}`}>{stage.label}</span>}
                    </div>
                    <div className="text-xs text-slate-500">{o.recipient_email} · {(o.items || []).length} item(s) · <span className="font-medium text-slate-700">${Number(lo ? lo.total : o.total).toFixed(2)}</span>{lo && lo.store_credit_used > 0 ? <span className="text-teal-600"> (quote ${Number(o.total).toFixed(2)} − ${Number(lo.store_credit_used).toFixed(2)} credit)</span> : null}{!o.reserved_order_id && o.expires_at ? <span className={isExpired ? 'text-orange-600' : 'text-slate-400'}> · {isExpired ? 'expired' : 'expires'} {String(o.expires_at).slice(0, 10)}</span> : null}</div>
                    {o.reserved_order_id && <div className="text-xs text-green-700 mt-0.5">Order {o.reserved_order_id}{lo && lo.tracking_number ? ` · Tracking ${lo.tracking_number}` : ''}</div>}
                    {partial && <div className="text-xs text-amber-700 mt-0.5" data-testid={`custom-order-balance-${o.custom_order_id}`}>Deposit ${Number(lo.amount_paid).toFixed(2)} · Balance due ${Number(lo.balance_due).toFixed(2)}</div>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(o)} title="Download PDF" data-testid={`custom-order-pdf-${o.custom_order_id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => duplicate(o)} title="Duplicate as new draft" data-testid={`custom-order-duplicate-${o.custom_order_id}`}><Copy className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => copyLink(o)} title="Copy customer link" data-testid={`custom-order-copy-${o.custom_order_id}`}><Link2 className="h-4 w-4" /></Button>
                    {isExpired && (
                      <Button variant="outline" size="sm" onClick={() => renew(o)} title="Renew quote" data-testid={`custom-order-renew-${o.custom_order_id}`}><RefreshCw className="h-4 w-4 mr-1" /> Renew</Button>
                    )}
                    {canSend && (
                      <Button variant="outline" size="sm" onClick={() => sendTest(o)} title="Send test to myself" data-testid={`custom-order-test-${o.custom_order_id}`}><Mail className="h-4 w-4" /></Button>
                    )}
                    {o.status !== 'reserved' && (
                      <Button variant="outline" size="sm" onClick={() => openEdit(o)} title="Edit" data-testid={`custom-order-edit-${o.custom_order_id}`}><Pencil className="h-4 w-4" /></Button>
                    )}
                    {canSend && (
                      <Button variant="outline" size="sm" onClick={() => openConvert(o)} title="Convert to order (no email)" data-testid={`custom-order-convert-${o.custom_order_id}`}><ShoppingCart className="h-4 w-4" /></Button>
                    )}
                    {canSend && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={() => sendEmail(o)} data-testid={`custom-order-send-${o.custom_order_id}`}>
                        <Send className="h-4 w-4 mr-1" /> {o.status === 'draft' ? 'Send' : 'Resend'}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(o)} title="Delete" data-testid={`custom-order-delete-${o.custom_order_id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>

                {/* Fulfillment controls for reserved orders */}
                {o.status === 'reserved' && (
                  <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`custom-order-fulfillment-${o.custom_order_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500 mr-1">Fulfillment:</span>
                      {lo && (
                        <span className="text-[11px] text-slate-400">
                          {isLocal ? 'Local delivery' : 'Shipping'}
                          {lo.payment_method_label ? ` · ${lo.payment_method_label}` : (lo.payment_method ? ` · ${lo.payment_method}` : '')}
                          {lo.store_credit_used > 0 ? ` · $${Number(lo.store_credit_used).toFixed(2)} credit` : ''}
                        </span>
                      )}
                      {!paid && !partial && !shipped && !delivered && (
                        <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={busyId === o.custom_order_id} onClick={() => { setConfirmOpen(confirmOpen === o.custom_order_id ? null : o.custom_order_id); setPartialOpen(null); setConfirmCredit({ apply: false, amount: '', accrue: false }); }} data-testid={`custom-order-confirm-payment-${o.custom_order_id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm Payment
                        </Button>
                      )}
                      {!paid && !partial && !shipped && !delivered && (
                        <Button variant="outline" size="sm" disabled={busyId === o.custom_order_id} onClick={() => { setPartialOpen(partialOpen === o.custom_order_id ? null : o.custom_order_id); setConfirmOpen(null); setPartialAmount(''); }} data-testid={`custom-order-partial-toggle-${o.custom_order_id}`}>
                          <DollarSign className="h-4 w-4 mr-1" /> Record Partial Payment
                        </Button>
                      )}
                      {partial && (
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={busyId === o.custom_order_id} onClick={() => markBalancePaid(o)} data-testid={`custom-order-balance-paid-${o.custom_order_id}`}>
                          <DollarSign className="h-4 w-4 mr-1" /> Mark Balance Paid (${Number(lo.balance_due).toFixed(2)})
                        </Button>
                      )}
                      {(paid || partial) && !shipped && !delivered && (
                        <>
                          <Button variant="outline" size="sm" disabled={busyId === o.custom_order_id} onClick={() => markPreparing(o)} data-testid={`custom-order-prepare-${o.custom_order_id}`}>
                            <PackageCheck className="h-4 w-4 mr-1" /> Notify: Preparing
                          </Button>
                          {isLocal ? (
                            <Button variant="outline" size="sm" disabled={busyId === o.custom_order_id} onClick={() => markDelivered(o)} data-testid={`custom-order-deliver-${o.custom_order_id}`}>
                              <MapPin className="h-4 w-4 mr-1" /> Mark Delivered
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setFulfillOpen(fulfillOpen === o.custom_order_id ? null : o.custom_order_id)} data-testid={`custom-order-ship-toggle-${o.custom_order_id}`}>
                              <Truck className="h-4 w-4 mr-1" /> Mark Shipped
                            </Button>
                          )}
                        </>
                      )}
                      {delivered && (
                        <span className="text-xs text-green-700 flex items-center gap-1"><MapPin className="h-4 w-4" /> Delivered</span>
                      )}
                      {shipped && (
                        <span className="text-xs text-green-700 flex items-center gap-1"><Truck className="h-4 w-4" /> Shipped{lo.tracking_number ? ` — ${lo.carrier_code?.toUpperCase() || ''} ${lo.tracking_number}` : ''}</span>
                      )}
                    </div>

                    {confirmOpen === o.custom_order_id && !paid && !partial && (
                      <div className="mt-3 bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`custom-order-confirm-panel-${o.custom_order_id}`}>
                        {o.customer_store_credit > 0 && (
                          <>
                            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                              <input type="checkbox" checked={confirmCredit.apply} onChange={(e) => setConfirmCredit({ apply: e.target.checked, amount: e.target.checked ? String(Math.min(Number(o.customer_store_credit), Number((lo && lo.total) || o.total || 0))) : '' })} data-testid={`custom-order-confirm-credit-${o.custom_order_id}`} />
                              Apply store/affiliate credit (${Number(o.customer_store_credit).toFixed(2)} available)
                            </label>
                            {confirmCredit.apply && (
                              <Input type="number" value={confirmCredit.amount} onChange={(e) => setConfirmCredit({ ...confirmCredit, amount: e.target.value })} className="h-9 w-40" placeholder="Amount" data-testid={`custom-order-confirm-credit-amount-${o.custom_order_id}`} />
                            )}
                          </>
                        )}
                        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`custom-order-confirm-accrue-box-${o.custom_order_id}`}>
                          <input type="checkbox" className="mt-0.5" checked={confirmCredit.accrue} onChange={(e) => setConfirmCredit({ ...confirmCredit, accrue: e.target.checked })} data-testid={`custom-order-confirm-accrue-${o.custom_order_id}`} />
                          <span>Accrue affiliate points on this order <span className="text-xs text-slate-400">(off by default)</span></span>
                        </label>
                        <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={busyId === o.custom_order_id} onClick={() => confirmPayment(o)} data-testid={`custom-order-confirm-paid-${o.custom_order_id}`}>
                          Confirm as Paid
                        </Button>
                      </div>
                    )}
                    {partialOpen === o.custom_order_id && !paid && !partial && (
                      <div className="mt-3 bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`custom-order-partial-panel-${o.custom_order_id}`}>
                        <Label className="text-[11px] text-slate-500">Amount the customer paid (order total ${Number((lo && lo.total) || o.total || 0).toFixed(2)})</Label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                            <Input type="number" min="0" step="0.01" value={partialAmount} onChange={(e) => setPartialAmount(e.target.value)} className="h-9 w-40 pl-5" placeholder="0.00" data-testid={`custom-order-partial-amount-${o.custom_order_id}`} />
                          </div>
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={busyId === o.custom_order_id} onClick={() => recordPartialPayment(o)} data-testid={`custom-order-partial-save-${o.custom_order_id}`}>
                            Record Payment
                          </Button>
                        </div>
                        <p className="text-[11px] text-slate-400">The order moves to "Being prepared" (shippable) and shows the balance still due. Both you and the customer get a receipt.</p>
                      </div>
                    )}
                    {fulfillOpen === o.custom_order_id && (paid || partial) && !shipped && !isLocal && (
                      <div className="mt-3 flex flex-wrap items-end gap-2 bg-slate-50 rounded-lg p-3">
                        <div>
                          <Label className="text-[11px] text-slate-500">Tracking number</Label>
                          <Input value={tracking.tracking_number} onChange={(e) => setTracking({ ...tracking, tracking_number: e.target.value })} className="h-9" data-testid={`custom-order-tracking-num-${o.custom_order_id}`} />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Carrier</Label>
                          <select className="border rounded-md h-9 px-2 text-sm" value={tracking.carrier_code} onChange={(e) => setTracking({ ...tracking, carrier_code: e.target.value })} data-testid={`custom-order-carrier-${o.custom_order_id}`}>
                            <option value="usps">USPS</option>
                            <option value="ups">UPS</option>
                            <option value="fedex">FedEx</option>
                            <option value="dhl">DHL</option>
                          </select>
                        </div>
                        <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={busyId === o.custom_order_id} onClick={() => markShipped(o)} data-testid={`custom-order-ship-confirm-${o.custom_order_id}`}>
                          Ship &amp; Notify
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Convert-to-order modal */}
      {convert && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="convert-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Convert to Order — {convert.recipient_name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setConvert(null)}><X className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-slate-500">Creates the order directly, without emailing the customer.</p>

            <div>
              <Label className="text-xs">Payment status</Label>
              <div className="flex gap-2 mt-1">
                {PAY_STATES.map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setConvertForm({ ...convertForm, payment_state: opt.v })}
                    className={`flex-1 border rounded-lg py-2 text-sm font-medium ${convertForm.payment_state === opt.v ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`}
                    data-testid={`convert-pay-${opt.v}`}>{opt.lbl}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Delivery method</Label>
              <div className="flex gap-2 mt-1">
                {DELIVERY_METHODS.map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setConvertForm({ ...convertForm, delivery_method: opt.v })}
                    className={`flex-1 border rounded-lg py-2 text-sm font-medium ${convertForm.delivery_method === opt.v ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`}
                    data-testid={`convert-delivery-${opt.v}`}>{opt.lbl}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment method</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={convertForm.payment_method} onChange={(e) => setConvertForm({ ...convertForm, payment_method: e.target.value })} data-testid="convert-method">
                  <option value="other">Other / Manual</option>
                  <option value="zelle">Zelle</option>
                  <option value="cash_app">Cash App</option>
                </select>
              </div>
              {convertForm.payment_method === 'other' && (
                <div>
                  <Label className="text-xs">Label (e.g. in person, wire)</Label>
                  <Input value={convertForm.payment_method_label} onChange={(e) => setConvertForm({ ...convertForm, payment_method_label: e.target.value })} data-testid="convert-method-label" />
                </div>
              )}
            </div>

            {convertForm.payment_state === 'deposit' && (
              <div>
                <Label className="text-xs">Deposit amount collected now ($)</Label>
                <Input type="number" value={convertForm.deposit_amount} onChange={(e) => setConvertForm({ ...convertForm, deposit_amount: e.target.value })} placeholder="0.00" data-testid="convert-deposit-amount" />
              </div>
            )}

            {convert.customer_store_credit > 0 && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="convert-credit-box">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={convertForm.apply_credit} onChange={(e) => setConvertForm({ ...convertForm, apply_credit: e.target.checked, credit_amount: e.target.checked ? String(Math.min(Number(convert.customer_store_credit), Number(convert.total || 0))) : '' })} data-testid="convert-apply-credit" />
                  Apply store/affiliate credit (${Number(convert.customer_store_credit).toFixed(2)} available)
                </label>
                {convertForm.apply_credit && (
                  <div>
                    <Label className="text-xs">Amount of credit to apply ($)</Label>
                    <Input type="number" value={convertForm.credit_amount} onChange={(e) => setConvertForm({ ...convertForm, credit_amount: e.target.value })} data-testid="convert-credit-amount" />
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 p-3" data-testid="convert-accrue-points-box">
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={convertForm.accrue_affiliate_points} onChange={(e) => setConvertForm({ ...convertForm, accrue_affiliate_points: e.target.checked })} data-testid="convert-accrue-points" />
                <span>Accrue affiliate points on this order<br /><span className="text-xs text-slate-400">Off by default — custom orders don't earn the recipient affiliate points unless you check this.</span></span>
              </label>
            </div>

            {(() => {
              const quoteTotal = Number(convert.total || 0);
              const credit = Number(convert.customer_store_credit || 0);
              const wantCredit = convertForm.apply_credit ? (convertForm.credit_amount === '' ? credit : (parseFloat(convertForm.credit_amount) || 0)) : 0;
              const applied = Math.max(0, Math.min(wantCredit, credit, quoteTotal));
              const due = Math.max(0, quoteTotal - applied);
              const deposit = convertForm.payment_state === 'deposit' ? Math.max(0, parseFloat(convertForm.deposit_amount) || 0) : (convertForm.payment_state === 'paid' ? due : 0);
              const balance = Math.max(0, due - deposit);
              return (
                <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1" data-testid="convert-credit-preview">
                  <div className="flex justify-between text-slate-600"><span>Quote total</span><span>${quoteTotal.toFixed(2)}</span></div>
                  {applied > 0 && (
                    <div className="flex justify-between text-teal-700" data-testid="convert-credit-note">
                      <span>Store/affiliate credit applied</span><span>− ${applied.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-slate-900 border-t pt-1"><span>Amount due</span><span data-testid="convert-amount-due">${due.toFixed(2)}</span></div>
                  {convertForm.payment_state === 'deposit' && (
                    <>
                      <div className="flex justify-between text-slate-600"><span>Deposit collected</span><span>− ${deposit.toFixed(2)}</span></div>
                      <div className="flex justify-between font-semibold text-amber-700" data-testid="convert-balance-due"><span>Balance due later</span><span>${balance.toFixed(2)}</span></div>
                    </>
                  )}
                  {applied > 0 && credit > applied && <div className="text-[11px] text-slate-400">${(credit - applied).toFixed(2)} credit will remain on the account.</div>}
                </div>
              );
            })()}

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">Address line 1 *</Label><Input value={convertForm.shipping_address.address_line1} onChange={(e) => setConvertForm({ ...convertForm, shipping_address: { ...convertForm.shipping_address, address_line1: e.target.value } })} data-testid="convert-addr-line1" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Address line 2</Label><Input value={convertForm.shipping_address.address_line2} onChange={(e) => setConvertForm({ ...convertForm, shipping_address: { ...convertForm.shipping_address, address_line2: e.target.value } })} /></div>
              <div><Label className="text-xs">City *</Label><Input value={convertForm.shipping_address.city} onChange={(e) => setConvertForm({ ...convertForm, shipping_address: { ...convertForm.shipping_address, city: e.target.value } })} data-testid="convert-addr-city" /></div>
              <div><Label className="text-xs">State *</Label><Input value={convertForm.shipping_address.state} onChange={(e) => setConvertForm({ ...convertForm, shipping_address: { ...convertForm.shipping_address, state: e.target.value } })} data-testid="convert-addr-state" /></div>
              <div><Label className="text-xs">ZIP *</Label><Input value={convertForm.shipping_address.zip_code} onChange={(e) => setConvertForm({ ...convertForm, shipping_address: { ...convertForm.shipping_address, zip_code: e.target.value } })} data-testid="convert-addr-zip" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={convertForm.phone} onChange={(e) => setConvertForm({ ...convertForm, phone: e.target.value })} data-testid="convert-phone" /></div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConvert(null)}>Cancel</Button>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white" disabled={busyId === convert.custom_order_id} onClick={submitConvert} data-testid="convert-submit">
                {busyId === convert.custom_order_id ? 'Converting…' : `Convert · ${{ paid: 'Paid', deposit: 'Deposit', reserved: 'Reserved' }[convertForm.payment_state]}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomOrdersTab;
